// @ts-nocheck

import React, { useMemo, createRef, useContext } from 'react';
import { Text, Group, Rect, Image, Circle, Line } from 'react-konva';

import Formatter from '../../../i18n/formatter';
import { __ } from '../../../i18n/l10n';
import BetshareContext from '../contexts/BetshareContext';
import { useGetHeightsFromRefs } from '../hooks';
import { getRelativeValue } from '../utils';
import { getOutcomeLabelPosition, getOutcomeColor, fragmentText } from '../utils';

import BankerBadge from './BankerBadge';
import { getBoundsTitle, getBoundsBetTitle, getBoundsLabel, getBoundsOdd } from './commonBounds';
import TextWithBackground from './TextWithBackground';

const areas = ['titleFirstLine', 'titleRest', 'date', 'logo'];

const OutcomeBetbuilder = (props) => {
  const { x, y, matchTitle, sportId, matchStart, odds, subOutcomes, forwardRef, isBanker } = props;

  const { images, stageWidth } = useContext(BetshareContext);

  const areasOutcomes = useMemo(() => {
    const refKeys = [];
    subOutcomes.forEach((outcome, index) => refKeys.push(`outcome${index}-betTitle`, `outcome${index}-outcomeLabel`));
    return refKeys;
  }, [subOutcomes]);

  const allRefs = useMemo(
    () => areas.concat(areasOutcomes).reduce((acc, cur) => ({ ...acc, [cur]: createRef() }), {}),
    [areasOutcomes],
  );

  const heights = useGetHeightsFromRefs(allRefs);
  const marginLeft = getRelativeValue(13, stageWidth);
  const marginTop = getRelativeValue(13, stageWidth);
  const marginLogoTop = getRelativeValue(6, stageWidth);
  const marginLogoBottom = getRelativeValue(6, stageWidth);
  const marginBetweenOutcome = getRelativeValue(6, stageWidth);

  const boundsTitle = getBoundsTitle(stageWidth, matchStart);
  const boundsBetTitle = getBoundsBetTitle(stageWidth);
  const boundsLabel = getBoundsLabel(stageWidth);
  const boundsOdd = getBoundsOdd(stageWidth);
  const isLabelUsingRemainingSpace = {};
  const outcomesHeights = [];

  const linesTitle = fragmentText(matchTitle.toUpperCase(), boundsTitle);
  const firstLineTitle = linesTitle.shift().text;
  const restLinesTitle = linesTitle.map((line) => line.text).join();

  const getLineY = (heights) => heights.slice(0, heights.length - 1).reduce((acc, cur) => acc + cur, 0);
  const getTotalOutcomesHeight = (heights) => heights.reduce((acc, cur) => acc + cur, 0);

  const sportIconProps = useMemo(
    () => ({
      image: (sportId && images[`sportIcon${sportId}`]) || images.fallbackSportIcon,
      x: marginLeft - getRelativeValue(4, stageWidth),
      y: -getRelativeValue(3, stageWidth),
      width: getRelativeValue(22, stageWidth),
      height: getRelativeValue(22, stageWidth),
    }),
    [sportId, marginLeft, stageWidth, images],
  );

  // define y positions
  const yDate = heights.titleFirstLine + heights.titleRest || 0;
  const yBetbuilderLogo = yDate + heights.date + marginLogoTop || 0;

  return (
    <Group x={x} y={y} ref={forwardRef}>
      {/* Empty Rect for margin top */}
      <Rect ref={allRefs.marginTop} x={0} y={0} width={1} height={marginTop} />
      {/* Badge for system bases */}
      {isBanker && <BankerBadge height={marginTop} stageWidth={stageWidth} />}
      {/* MyMatch section */}
      <Group x={0} y={marginTop}>
        <Image {...sportIconProps} />
        <Text
          ref={allRefs.titleFirstLine}
          text={firstLineTitle}
          x={getRelativeValue(33, stageWidth)}
          y={0}
          width={boundsTitle.width}
          fontSize={boundsTitle.fontSize}
          lineHeight={boundsTitle.lineHeight}
          fontFamily={boundsTitle.fontFamily}
          fill="#fff"
        />
        {restLinesTitle !== '' && restLinesTitle !== ' ' ? (
          <Text
            ref={allRefs.titleRest}
            text={restLinesTitle}
            x={marginLeft}
            y={boundsTitle.lineHeight * boundsTitle.fontSize}
            width={getRelativeValue(270, stageWidth)}
            fontSize={boundsTitle.fontSize}
            lineHeight={boundsTitle.lineHeight}
            fontFamily={boundsTitle.fontFamily}
            fill="#fff"
          />
        ) : (
          <Rect ref={allRefs.titleRest} x={0} y={0} width={0} height={0} />
        )}
        {matchStart ? (
          <Text
            ref={allRefs.date}
            text={Formatter.formatDate(matchStart, 'dateSlash')}
            x={marginLeft}
            y={yDate}
            width={getRelativeValue(300, stageWidth)}
            fontSize={getRelativeValue(13, stageWidth)}
            lineHeight={1.2}
            fontFamily="BettingPoliticaExtraBold, sans-serif"
            fill="#a9a9a9"
          />
        ) : (
          <Rect ref={allRefs.date} x={0} y={0} width={0} height={0} />
        )}
        <Image
          ref={allRefs.logo}
          image={images.betbuilderLogo}
          x={marginLeft}
          y={yBetbuilderLogo}
          width={getRelativeValue(68, stageWidth)}
          height={getRelativeValue(10, stageWidth)}
        />
        {/* Outcomes */}
        {subOutcomes.map((outcome, index) => {
          const { betTitle, outcomeLabel, outcomeHasOccured, isCancelled } = outcome;

          const finalBetTitle = __('%1$s:', betTitle);

          const backgroundLabel = getOutcomeColor(outcomeHasOccured, isCancelled);

          const { xLabel, yLabel, labelWidth, isUsingRemainingSpace } = getOutcomeLabelPosition(
            finalBetTitle,
            boundsBetTitle,
            outcomeLabel,
            boundsLabel,
          );
          const xOutcomeLabel = getRelativeValue(33, stageWidth) + xLabel;
          isLabelUsingRemainingSpace[index] = isUsingRemainingSpace;
          let currentOutcomeHeight = heights[`outcome${index}-betTitle`] + marginBetweenOutcome;
          if (!isUsingRemainingSpace) {
            currentOutcomeHeight += heights[`outcome${index}-outcomeLabel`];
          }
          outcomesHeights.push(currentOutcomeHeight || 0);

          let idx = 0;
          const heightPreviousOutcomes = subOutcomes.slice(0, index).reduce((acc) => {
            const outcomeHeight = isLabelUsingRemainingSpace[idx]
              ? heights[`outcome${idx}-betTitle`]
              : heights[`outcome${idx}-betTitle`] + heights[`outcome${idx}-outcomeLabel`];
            const returnValue = acc + outcomeHeight + marginBetweenOutcome;
            idx++;
            return returnValue;
          }, 0);
          const yOutcome = yBetbuilderLogo + heights.logo + marginLogoBottom + heightPreviousOutcomes || 0;

          // Side note, set x and y on following Group element break height computation
          return (
            <Group key={`${betTitle}-${outcomeLabel}`}>
              {/* Left side bullet */}
              <Circle
                x={getRelativeValue(19, stageWidth)}
                y={yOutcome + getRelativeValue(8, stageWidth)}
                radius={getRelativeValue(4, stageWidth)}
                fill="#39393e"
              />
              <Text
                ref={allRefs[`outcome${index}-betTitle`]}
                text={betTitle ? finalBetTitle : null}
                x={getRelativeValue(33, stageWidth)}
                y={yOutcome}
                width={boundsBetTitle.width}
                fontSize={boundsBetTitle.fontSize}
                lineHeight={boundsBetTitle.lineHeight}
                fontFamily={boundsBetTitle.fontFamily}
                fill="#fff"
              />
              <TextWithBackground
                ref={allRefs[`outcome${index}-outcomeLabel`]}
                text={outcomeLabel}
                x={xOutcomeLabel}
                y={yOutcome + yLabel}
                width={labelWidth}
                fontSize={boundsLabel.fontSize}
                lineHeight={boundsLabel.lineHeight}
                fontFamily={boundsLabel.fontFamily}
                backgroundColor={backgroundLabel}
                paddingTopBottom={boundsLabel.paddingTop}
                paddingLeftRight={boundsLabel.paddingLeft}
                cornerRadius={getRelativeValue(2, stageWidth)}
                fill="#fff"
              />
            </Group>
          );
        })}

        {/* Left side line which pass through all bullets */}
        <Line
          x={getRelativeValue(19, stageWidth)}
          y={yBetbuilderLogo + heights.logo + marginLogoBottom + getRelativeValue(8, stageWidth) || 0}
          stroke="#39393e"
          strokeWidth={2}
          points={[0, 0, 0, getLineY(outcomesHeights)]}
        />

        {/*  Odds */}
        <Text
          text={Formatter.formatOdd(odds)}
          x={boundsOdd.x}
          y={yBetbuilderLogo + marginLogoBottom + getTotalOutcomesHeight(outcomesHeights) - boundsOdd.fontSize || 0}
          fontSize={boundsOdd.fontSize}
          lineHeight={boundsOdd.lineHeight}
          fontFamily={boundsOdd.fontFamily}
          align="center"
          width={boundsOdd.width}
          height={boundsOdd.height}
          fill="#fff"
        />

        {/* Empty Rect for margin bottom */}
        <Rect
          x={0}
          y={
            yBetbuilderLogo + heights.logo + marginLogoBottom + getTotalOutcomesHeight(outcomesHeights) + marginTop || 0
          }
          width={1}
          height={1}
        />
      </Group>
    </Group>
  );
};

export default OutcomeBetbuilder;
